import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { fcfa, fdate, fdatetime } from "@/lib/format";
import { purposeLabel, methodLabel, statusInfo } from "@/lib/i18n";
import { EmptyState, Loader, PageTitle, StatusBadge } from "@/components/common";
import { DataView } from "@/components/console/DataView";
import { PaymentsView } from "@/pages/console/Records";

const opts = (keys, fn = (k) => statusInfo(k)[0]) => keys.map((k) => [k, fn(k)]);
const badge = (k) => ({ key: k, label: "Statut", value: (r) => statusInfo(r[k])[0], render: (r) => <StatusBadge status={r[k]} /> });

export function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  if (!s) return <Loader />;
  const cards = [
    ["Membres actifs", s.members.actif, "dash-active"], ["Suspendus", s.members.suspendu, "dash-suspended"], ["Candidats", s.members.candidat, "dash-candidates"],
    ["Demandes nouvelles", s.invitation_requests_new, "dash-requests"], ["Paiements à valider", s.payments_pending, "dash-pending"], ["Entrées d'audit", s.audit_entries, "dash-audit"],
  ];
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Console" title="Tableau de bord" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-mr-border border border-mr-border">
        {cards.map(([l, v, id]) => (
          <div key={id} className="bg-mr-surface p-6" data-testid={id}>
            <p className="text-xs uppercase tracking-wider text-mr-muted">{l}</p>
            <p className="font-display font-bold text-4xl tabular mt-2">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Summary({ d }) {
  return (
    <div className="space-y-6">
      <div className="mr-card" data-testid="finances-club-revenue">
        <p className="text-xs uppercase tracking-wider text-mr-muted">Total des recettes propres (compte de l'association)</p>
        <p className="font-display font-bold text-4xl tabular mt-2" data-testid="finances-club-total">{fcfa(d.club_revenue_total)}</p>
        <ul className="mt-5 divide-y divide-mr-border">
          {Object.entries(d.club_revenue).map(([k, v]) => (
            <li key={k} className="flex justify-between py-2 text-sm"><span className="text-mr-text2">{purposeLabel(k)} ({v.count})</span><span className="tabular">{fcfa(v.total)}</span></li>
          ))}
        </ul>
      </div>
      <div className="mr-card grid sm:grid-cols-2 gap-6" data-testid="finances-breakdown">
        <div><p className="font-medium mb-2">Recettes du club par mois</p>{Object.entries(d.by_month || {}).map(([m, v]) => <p key={m} className="flex justify-between text-sm tabular"><span>{m}</span><span>{fcfa(v)}</span></p>)}</div>
        <div><p className="font-medium mb-2">Par moyen de paiement</p>{Object.entries(d.by_method || {}).map(([m, v]) => <p key={m} className="flex justify-between text-sm tabular"><span>{methodLabel(m)}</span><span>{fcfa(v)}</span></p>)}</div>
      </div>
    </div>
  );
}

function Funds({ d }) {
  const nav = useNavigate();
  const rows = d.operation_funds.map((o) => ({ ...o, id: o.operation_id, balance: o.in - o.out }));
  if (!rows.length) return <EmptyState icon={Briefcase} title="Aucun fonds d'opération suivi" text="Les entrées et sorties des comptes d'opération apparaîtront ici." testId="finances-operation-empty" />;
  return (
    <>
      <p className="text-sm text-mr-text2 mb-4">Ces montants sont versés sur les comptes dédiés des opérations. Ils ne font jamais partie des recettes du club.</p>
      <DataView page="funds" title="Fonds des opérations" rows={rows} onRowClick={(o) => nav(`/app/operations/${o.id}?tab=pilotage`)}
        columns={[{ key: "title", label: "Opération", className: "font-medium" }, { key: "in", label: "Entrées", value: (o) => fcfa(o.in), className: "tabular" },
          { key: "out", label: "Sorties", value: (o) => fcfa(o.out), className: "tabular" }, { key: "balance", label: "Solde suivi", value: (o) => fcfa(o.balance), className: "tabular" }]}
        kpis={[{ key: "ops", label: "Opérations", value: (r) => r.length }, { key: "in", label: "Entrées", value: (r) => fcfa(r.reduce((s, o) => s + o.in, 0)) },
          { key: "out", label: "Sorties", value: (r) => fcfa(r.reduce((s, o) => s + o.out, 0)) }, { key: "bal", label: "Solde suivi", value: (r) => fcfa(r.reduce((s, o) => s + o.balance, 0)) }]} />
    </>
  );
}

function Reconciliations() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/reconciliations").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Loader />;
  return (
    <DataView page="reconciliations" title="Rapprochements" rows={rows} empty="Aucun rapprochement" onRowClick={(r) => nav(`/app/operations/${r.operation_id}?tab=pilotage`)}
      columns={[{ key: "operation", label: "Opération", className: "font-medium" }, { key: "period", label: "Période", value: (r) => `${fdate(r.period_start)} → ${fdate(r.period_end)}`, className: "tabular whitespace-nowrap" },
        { key: "matched", label: "Rapprochés", className: "tabular" }, { key: "unmatched", label: "Non rapprochés", className: "tabular" }, { key: "gap", label: "Écart", value: (r) => fcfa(r.gap), className: "tabular" },
        badge("status"), { key: "validated_at", label: "Validé le", value: (r) => (r.validated_at ? fdate(r.validated_at) : "") }]}
      filters={[{ key: "status", label: "Statut", options: [...new Set(rows.map((r) => r.status))].map((s) => [s, statusInfo(s)[0]]) }, { key: "operation", label: "Opération", options: [...new Set(rows.map((r) => r.operation))].map((o) => [o, o]) }]}
      kpis={[{ key: "count", label: "Rapprochements", value: (r) => r.length }, { key: "gaps", label: "Avec écart", value: (r) => r.filter((x) => x.gap !== 0).length },
        { key: "todo", label: "À valider", value: (r) => r.filter((x) => !x.validated_at).length }, { key: "sum", label: "Écart total", value: (r) => fcfa(r.reduce((s, x) => s + Math.abs(x.gap), 0)) }]} />
  );
}

function Receipts() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/receipts").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Loader />;
  return (
    <DataView page="receipts" title="Reçus" rows={rows} empty="Aucun reçu"
      columns={[{ key: "number", label: "N°", className: "tabular font-medium" }, { key: "member", label: "Membre" }, { key: "entity", label: "Émetteur" }, { key: "purpose", label: "Objet", value: (r) => purposeLabel(r.purpose) },
        { key: "method", label: "Moyen", value: (r) => methodLabel(r.method) }, { key: "amount", label: "Montant", value: (r) => fcfa(r.amount), className: "tabular" }, { key: "issued_at", label: "Émis le", value: (r) => fdatetime(r.issued_at), className: "tabular whitespace-nowrap" }]}
      filters={[{ key: "purpose", label: "Objet", options: opts([...new Set(rows.map((r) => r.purpose))], purposeLabel) }, { key: "method", label: "Moyen", options: opts([...new Set(rows.map((r) => r.method))], methodLabel) }]}
      kpis={[{ key: "count", label: "Reçus émis", value: (r) => r.length }, { key: "total", label: "Montant total", value: (r) => fcfa(r.reduce((s, x) => s + (x.amount || 0), 0)) }]} />
  );
}

const FIN_TABS = [["synthese", "Synthèse"], ["cotisations", "Cotisations"], ["a-valider", "Paiements à valider"], ["fonds", "Fonds des opérations"], ["rapprochements", "Rapprochements"], ["recus", "Reçus"]];

export function Finances() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("onglet") || "synthese";
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/finances").then(({ data }) => setD(data)).catch(() => {});
  }, []);
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Finances" title="Finances du club" />
      <div className="flex gap-1 overflow-x-auto border-b border-mr-border mb-6" data-testid="finances-tabs">{FIN_TABS.map(([k, l]) => (
        <button key={k} data-testid={`finances-tab-${k}`} onClick={() => setSp({ onglet: k })} className={`shrink-0 px-3 min-h-[44px] text-sm border-b-2 -mb-px ${tab === k ? "border-mr-accent text-mr-text" : "border-transparent text-mr-muted"}`}>{l}</button>))}</div>
      {tab === "cotisations" && <PaymentsView page="fin-cotisations" title="Cotisations" params={{ purpose: "cotisation_annuelle" }} />}
      {tab === "a-valider" && <PaymentsView page="fin-pending" title="Paiements à valider" params={{ status: "en_attente_validation" }} />}
      {tab === "rapprochements" && <Reconciliations />}
      {tab === "recus" && <Receipts />}
      {["synthese", "fonds"].includes(tab) && (!d ? <Loader /> : tab === "synthese" ? <Summary d={d} /> : <Funds d={d} />)}
    </div>
  );
}

const OP_STATUSES = ["brouillon", "soumis", "publie", "adhesions_closes", "en_execution", "cloture", "annulee", "suspendue"];
const OP_LABEL = { brouillon: "Brouillon", soumis: "À valider", publie: "Publiée", adhesions_closes: "Adhésions closes", en_execution: "En exécution", cloture: "Clôturée", annulee: "Annulée", suspendue: "Suspendue" };
const OP_TYPES = { projet: "Projet collectif", achat_groupe: "Achat groupé", voyage: "Voyage", evenement: "Événement" };

export function ConsoleOperations() {
  const nav = useNavigate();
  const [ops, setOps] = useState(null);
  useEffect(() => {
    api.get("/admin/operations").then(({ data }) => setOps(data)).catch(() => setOps([]));
  }, []);
  const count = (r, ss) => r.filter((o) => ss.includes(o.status)).length;
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Opérations" title="Opérations" />
      {ops === null ? <Loader /> : (
        <DataView page="operations" title="Opérations" rows={ops} empty="Aucune opération" onRowClick={(o) => nav(`/app/operations/${o.id}?tab=pilotage`)}
          columns={[{ key: "title", label: "Titre", className: "font-medium" }, { key: "type", label: "Type", value: (o) => OP_TYPES[o.type] || o.type }, { key: "status", label: "Statut", value: (o) => OP_LABEL[o.status] || o.status, render: (o) => <StatusBadge status={o.status} label={OP_LABEL[o.status]} /> },
            { key: "participants", label: "Participants", className: "tabular" }, { key: "share_amount", label: "Part", value: (o) => (o.share_amount ? fcfa(o.share_amount) : ""), className: "tabular", hidden: true },
            { key: "created_at", label: "Créée le", value: (o) => fdate(o.created_at), className: "tabular" },
            { key: "flag", label: "À traiter", value: (o) => (o.pending_account ? "Changement de compte à valider" : o.status === "soumis" ? "À valider" : ""), className: "text-xs text-mr-warning" }]}
          filters={[{ key: "status", label: "Statut", options: opts(OP_STATUSES, (k) => OP_LABEL[k]) }, { key: "type", label: "Type", options: Object.entries(OP_TYPES) }]}
          kanban={{ get: (o) => o.status, columns: opts(OP_STATUSES, (k) => OP_LABEL[k]) }}
          kpis={[{ key: "total", label: "Opérations", value: (r) => r.length }, { key: "todo", label: "À valider", value: (r) => count(r, ["soumis"]) + r.filter((o) => o.pending_account).length },
            { key: "live", label: "En cours", value: (r) => count(r, ["publie", "adhesions_closes", "en_execution"]) }, { key: "done", label: "Clôturées", value: (r) => count(r, ["cloture"]) }]} />
      )}
    </div>
  );
}

export function Requests() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/invitation-requests").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Admission" title="Demandes d'invitation" />
      {items === null ? <Loader /> : (
        <DataView page="requests" title="Demandes d'invitation" rows={items} empty="Aucune demande"
          columns={[{ key: "name", label: "Nom", className: "font-medium" }, { key: "created_at", label: "Date", value: (r) => fdatetime(r.created_at), className: "tabular whitespace-nowrap" }, { key: "phone", label: "Téléphone", className: "tabular" },
            { key: "email", label: "E-mail", hidden: true }, { key: "company", label: "Entreprise" }, { key: "sector", label: "Secteur" }, { key: "city", label: "Ville" }, { key: "seniority", label: "Ancienneté", hidden: true },
            { key: "seeks", label: "Cherche", className: "max-w-[220px] text-mr-text2" }, badge("status")]}
          filters={[{ key: "status", label: "Statut", options: [...new Set(items.map((r) => r.status))].map((s) => [s, statusInfo(s)[0]]) }, { key: "sector", label: "Secteur", options: [...new Set(items.map((r) => r.sector).filter(Boolean))].map((s) => [s, s]) },
            { key: "city", label: "Ville", options: [...new Set(items.map((r) => r.city).filter(Boolean))].map((s) => [s, s]) }]}
          kpis={[{ key: "total", label: "Demandes", value: (r) => r.length }, { key: "new", label: "Nouvelles", value: (r) => r.filter((x) => x.status === "nouvelle").length },
            { key: "study", label: "En étude", value: (r) => r.filter((x) => x.status === "en_etude").length }, { key: "month", label: "30 derniers jours", value: (r) => r.filter((x) => Date.now() - new Date(x.created_at) < 2592e6).length }]} />
      )}
    </div>
  );
}
